import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Popper from "@mui/material/Popper";
import React, { useEffect, useRef, useState } from "react";
import PiMenuButton from "./PiMenuButton";

interface PiMenuSubmenuProps {
  text: string;
  icon?: React.ReactNode;
  /** `variant="modern"` PiMenuButtons. Clicking one closes the whole menu. */
  children: React.ReactNode;
}

// Long enough to move the pointer diagonally onto the submenu without it closing.
const CLOSE_DELAY_MS = 250;

/**
 * A `modernMenu` row that folds out a second menu to its side. Opens on hover
 * and on click, so it works the same with a mouse, a touchpad or the keyboard.
 *
 * The submenu is rendered inside the parent PiPopoverMenu (disablePortal): that
 * keeps it within the popover's focus trap, and lets clicks on its items bubble
 * to the parent's close handler like any other item.
 */
export default function PiMenuSubmenu({ text, icon, children }: PiMenuSubmenuProps) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number>();
  const [open, setOpen] = useState(false);

  const cancelClose = () => window.clearTimeout(closeTimer.current);
  const openNow = () => {
    cancelClose();
    setOpen(true);
  };
  const closeSoon = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };
  useEffect(() => cancelClose, []);

  return (
    <>
      <div
        ref={anchorRef}
        onMouseEnter={openNow}
        onMouseLeave={closeSoon}
        // Opening the submenu must not close the parent menu.
        onClick={(event) => event.stopPropagation()}
      >
        <PiMenuButton
          text={text}
          icon={icon}
          variant="modern"
          endIcon={<ChevronRightIcon color="inherit" fontSize="inherit" />}
          submenuOpen={open}
          onClick={() => {
            cancelClose();
            setOpen((current) => !current);
          }}
        />
      </div>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="right-start"
        disablePortal
        // Line the first item up with the trigger row (the panel has 6px padding).
        modifiers={[{ name: "offset", options: { offset: [-6, 8] } }]}
        onMouseEnter={openNow}
        onMouseLeave={closeSoon}
        style={{ zIndex: 1 }}
      >
        <div
          role="menu"
          className="flex flex-col"
          style={{
            // Same panel as PiPopoverMenu's modernMenu.
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
            padding: "6px",
            minWidth: "180px",
            backgroundColor: "var(--pi-surface)",
          }}
        >
          {children}
        </div>
      </Popper>
    </>
  );
}
